import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { downloadDesignBackup, downloadClientBackup, restoreBackup } from '../../lib/api';
import { refreshBranding } from '../../lib/branding';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Download, Upload, Database, Palette, AlertTriangle, CheckCircle2, Sparkles,
  RefreshCcw, FilePlus, Layers,
} from 'lucide-react';
import { motion } from 'framer-motion';

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try {
      if (a.parentNode) a.parentNode.removeChild(a);
    } catch (_) { /* node already detached */ }
    URL.revokeObjectURL(url);
  }, 200);
}

function getFilenameFromHeaders(headers, fallback) {
  const cd = headers?.['content-disposition'] || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return (m && m[1]) || fallback;
}

export default function AdminBackup() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [downloadingDesign, setDownloadingDesign] = useState(false);
  const [downloadingClient, setDownloadingClient] = useState(false);

  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('replace');
  const [confirmation, setConfirmation] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) navigate('/admin/login');
  }, [user, isAdmin, authLoading, navigate]);

  if (!user || !isAdmin) return null;

  const handleDownload = async (kind) => {
    try {
      const setter = kind === 'design' ? setDownloadingDesign : setDownloadingClient;
      setter(true);
      const fn = kind === 'design' ? downloadDesignBackup : downloadClientBackup;
      const res = await fn();
      const fallback = kind === 'design' ? 'winluxor-design.zip' : 'winluxor-clients.json';
      const filename = getFilenameFromHeaders(res.headers, fallback);
      triggerDownload(res.data, filename);
      toast.success(t('backupDownloaded') || 'Backup downloaded');
    } catch (e) {
      console.error(e);
      toast.error(t('backupDownloadFailed') || 'Download failed');
    } finally {
      setDownloadingDesign(false);
      setDownloadingClient(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setRestoreFile(f);
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    if (confirmation !== 'RESTORE') {
      toast.error(t('typeRestoreToConfirm') || 'Type RESTORE to confirm');
      return;
    }
    setRestoring(true);
    setProgress(0);
    setLastResult(null);
    try {
      const res = await restoreBackup(restoreFile, restoreMode, (e) => {
        if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
      });
      setLastResult(res.data);
      toast.success(`${t('restoreSuccess') || 'Restore complete'} (${res.data.type})`);
      setConfirmation('');
      setRestoreFile(null);
      // If design restored, refresh branding
      if (res.data.type === 'design') {
        await refreshBranding();
      }
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || e.message || 'Restore failed';
      toast.error(detail);
    } finally {
      setRestoring(false);
      setProgress(0);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="flex min-h-screen bg-rolex-green" data-testid="admin-backup" style={{ color: 'hsl(43 38% 92%)' }}>
      <AdminSidebar />
      <main className="flex-1 px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-10">
          <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> {t('backupSubtitle') || 'Migration & Recovery'}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight text-cream">
            {t('backupTitle') || 'Backups'}
          </h1>
          <p className="text-cream/60 mt-2 text-sm max-w-2xl">
            {t('backupHelp') || 'Download complete site snapshots (design + client data) and re-import them on any server. Everything you need for migration or disaster recovery.'}
          </p>
        </motion.div>

        {/* Download cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
          <Card className="bg-cream border border-[hsl(var(--gold)/0.30)] gold-glow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-rolex-green">
                <Palette className="w-5 h-5 text-gold" /> {t('backupDesignTitle') || 'Design Backup'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-xs text-rolex-green/70 space-y-1.5 mb-5">
                <li>✓ {t('backupDesignItem1') || 'Site settings & branding (logos, colors, titles)'}</li>
                <li>✓ {t('backupDesignItem2') || 'Raffles structure (titles, descriptions, packages)'}</li>
                <li>✓ {t('backupDesignItem3') || 'Point packs, VIP plans, promo codes'}</li>
                <li>✓ {t('backupDesignItem4') || 'Banners, newsletters, CMS pages'}</li>
                <li>✓ {t('backupDesignItem5') || 'All uploaded images (logos, raffle photos)'}</li>
              </ul>
              <Button
                className="w-full btn-gold rounded-xl gap-2 h-12 font-bold tracking-wide uppercase"
                onClick={() => handleDownload('design')}
                disabled={downloadingDesign}
                data-testid="download-design-btn"
              >
                <Download className="w-4 h-4" />
                {downloadingDesign ? (t('preparing') || 'Preparing...') : (t('downloadDesignZip') || 'Download Design ZIP')}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-cream border border-[hsl(var(--gold)/0.30)] gold-glow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-rolex-green">
                <Database className="w-5 h-5 text-gold" /> {t('backupClientsTitle') || 'Client Data Backup'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-xs text-rolex-green/70 space-y-1.5 mb-5">
                <li>✓ {t('backupClientsItem1') || 'All user accounts (including admin)'}</li>
                <li>✓ {t('backupClientsItem2') || 'Points balance, transactions, history'}</li>
                <li>✓ {t('backupClientsItem3') || 'Raffle entries & participations'}</li>
                <li>✓ {t('backupClientsItem4') || 'Notifications, wins, subscriptions'}</li>
                <li>✓ {t('backupClientsItem5') || 'Chat messages, email queue, referrals'}</li>
              </ul>
              <Button
                className="w-full btn-gold rounded-xl gap-2 h-12 font-bold tracking-wide uppercase"
                onClick={() => handleDownload('clients')}
                disabled={downloadingClient}
                data-testid="download-clients-btn"
              >
                <Download className="w-4 h-4" />
                {downloadingClient ? (t('preparing') || 'Preparing...') : (t('downloadClientsJson') || 'Download Clients JSON')}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Restore section */}
        <Card className="bg-cream border-2 border-red-300/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> {t('restoreZone') || 'Danger Zone — Restore'}
            </CardTitle>
            <p className="text-xs text-rolex-green/70 mt-1">
              {t('restoreHelp') || 'Drop a backup file below. The system auto-detects the type (design ZIP or client JSON).'}
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 sm:p-10 text-center transition-all ${
                dragOver
                  ? 'border-gold bg-[hsl(var(--gold)/0.08)] scale-[1.01]'
                  : 'border-[hsl(var(--gold)/0.4)] bg-[hsl(43_30%_94%)] hover:border-gold hover:bg-[hsl(var(--gold)/0.05)]'
              }`}
              data-testid="restore-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.json,application/zip,application/json"
                className="hidden"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              />
              <FilePlus className="w-12 h-12 text-gold mx-auto mb-3" />
              {restoreFile ? (
                <div>
                  <p className="font-semibold text-rolex-green text-base">{restoreFile.name}</p>
                  <p className="text-xs text-rolex-green/60 mt-1">{formatSize(restoreFile.size)}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-xs"
                    onClick={(e) => { e.stopPropagation(); setRestoreFile(null); }}
                  >
                    {t('removeFile') || 'Remove'}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="font-semibold text-rolex-green">
                    {t('dropOrClick') || 'Drop a backup file here, or click to browse'}
                  </p>
                  <p className="text-xs text-rolex-green/55 mt-1.5">
                    {t('supportedFormats') || 'Supported: .zip (design) or .json (clients) — up to 1 GB'}
                  </p>
                </>
              )}
            </div>

            {/* Mode toggle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRestoreMode('replace')}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${
                  restoreMode === 'replace'
                    ? 'border-red-400 bg-red-50'
                    : 'border-[hsl(var(--gold)/0.25)] bg-[hsl(43_30%_94%)] hover:border-[hsl(var(--gold)/0.5)]'
                }`}
                data-testid="mode-replace"
              >
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCcw className="w-4 h-4 text-red-600" />
                  <span className="font-semibold text-rolex-green text-sm">
                    {t('modeReplace') || 'Replace (recommended)'}
                  </span>
                </div>
                <p className="text-xs text-rolex-green/65">
                  {t('modeReplaceHelp') || 'Wipe existing collections and overwrite. Best for full migration.'}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setRestoreMode('merge')}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${
                  restoreMode === 'merge'
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-[hsl(var(--gold)/0.25)] bg-[hsl(43_30%_94%)] hover:border-[hsl(var(--gold)/0.5)]'
                }`}
                data-testid="mode-merge"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Layers className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-rolex-green text-sm">
                    {t('modeMerge') || 'Merge (keep existing)'}
                  </span>
                </div>
                <p className="text-xs text-rolex-green/65">
                  {t('modeMergeHelp') || 'Add backup data without deleting current. Skips duplicates by ID.'}
                </p>
              </button>
            </div>

            {/* Confirmation typing */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-rolex-green">
                {t('confirmRestoreLabel') || 'Type RESTORE to confirm'}
              </Label>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                placeholder="RESTORE"
                className="rounded-xl font-mono uppercase"
                data-testid="confirm-input"
              />
            </div>

            {/* Progress */}
            {restoring && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-rolex-green/70">
                  <span>{t('uploading') || 'Uploading...'}</span>
                  <span className="font-mono">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}

            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white btn-press rounded-xl h-12 font-bold tracking-wide uppercase gap-2"
              onClick={handleRestore}
              disabled={!restoreFile || confirmation !== 'RESTORE' || restoring}
              data-testid="restore-btn"
            >
              <Upload className="w-4 h-4" />
              {restoring ? (t('restoring') || 'Restoring...') : (t('restoreNow') || 'Restore now')}
            </Button>

            {lastResult && (
              <div className="rounded-xl border border-green-400/40 bg-green-50 p-4 space-y-2">
                <div className="flex items-center gap-2 text-green-800 font-semibold text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  {t('restoreSuccessTitle') || 'Restore completed'}
                  <span className="text-xs font-mono bg-green-200 text-green-900 px-2 py-0.5 rounded">
                    {lastResult.type} · {lastResult.mode}
                  </span>
                </div>
                <div className="text-xs text-rolex-green/75 grid grid-cols-2 gap-x-3 gap-y-1">
                  {Object.entries(lastResult.collections || {}).map(([col, info]) => (
                    <div key={col} className="flex justify-between">
                      <span className="font-medium">{col}</span>
                      <span className="font-mono text-green-800">+{info.inserted}</span>
                    </div>
                  ))}
                </div>
                {lastResult.uploads_restored > 0 && (
                  <div className="text-xs text-rolex-green/75 pt-1 border-t border-green-300/40">
                    {t('uploadsRestored') || 'Files restored'}: <span className="font-mono">{lastResult.uploads_restored}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
